import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid2 as Grid,
  Card,
  CardMedia,
  CardContent,
  Popover,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LoadPermitTemplateModal from "./components/LoadPermitTemplateModal";
import { ntboRelations, pcaRelations } from "../../../data/permitFormsData";

const SelectFormsStep = ({
  selectedPermitType,
  setSelectedPermitType,
  selectedForms,
  setSelectedForms,
  stormwaterSelection,
  setStormwaterSelection,
  availableForms,
  setDisplayImage,
  cities,
  selectedCity,
  setSelectedCity,
  countyLoading,
  groupedForms,
  privateProviderSignerType,
  setPrivateProviderSignerType,
  county,
  contractors,
  formContractors,
  setFormContractors,
}) => {
  const GROUPS = [ntboRelations, pcaRelations];
  const stormwaterList = ["Individual", "Corporation", "LLC"];
  const privProviderList = ["Individual", "Corporation", "Partnership"];
  const stormwaterMapping = {
    Individual: "stormwater_indiv",
    Corporation: "stormwater_corp",
    LLC: "stormwater_llc",
  };

  const availablePermitTypes = useSelector(
    (state) => state.nocData?.available_permit_types,
  );
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [privFormAnchorEl, setPrivFormAnchorEl] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const cardsAreaRef = useRef(null);
  const stormwaterTriggerRef = useRef(null);
  const shouldOpenStormwaterRef = useRef(false);

  // Helper function to filter contractors by license type for specific forms
  const getFilteredContractors = useCallback(
    (formName) => {
      if (!contractors || contractors.length === 0) return [];

      // Define license type prefixes for each form
      // TTT = Demo licenses (work for all types)
      const licenseFilters = {
        electrical_permit: ["EC", "ER", "TTT"], // Electrical contractors + demo licenses
        gas_permit: ["CMC", "RM", "CGC", "CBC", "CRC", "RG", "RB", "RR", "TTT"], // Mechanical + General contractors + demo licenses
        noc: null, // Any contractor (no filtering)
      };

      const filter = licenseFilters[formName];

      // If no filter (like NOC), return all contractors
      let filteredList = filter
        ? contractors.filter((contractor) => {
            const licenseNumber =
              contractor?.licenseNumber || contractor?.license_number || "";
            return filter.some((prefix) => licenseNumber.startsWith(prefix));
          })
        : contractors;

      // Deduplicate by license number to avoid showing same contractor multiple times
      const seen = new Map();
      const deduplicated = [];

      for (const contractor of filteredList) {
        const licenseNumber =
          contractor?.licenseNumber || contractor?.license_number || "";
        if (licenseNumber && !seen.has(licenseNumber)) {
          seen.set(licenseNumber, true);
          deduplicated.push(contractor);
        }
      }

      return deduplicated;
    },
    [contractors],
  );

  const getContractorDisplayName = useCallback((contractor) => {
    if (!contractor) return "";
    return (
      contractor?.businessName ||
      contractor?.dba_name ||
      contractor?.primary_name ||
      `${contractor?.firstName || contractor?.first_name || ""} ${
        contractor?.lastName || contractor?.last_name || ""
      }`
    );
  }, []);

  const normalizeContractorName = useCallback((name) => {
    if (!name) return "";
    return name.toLowerCase().replace(/\s+/g, " ").trim();
  }, []);

  // TEMPORARY: Lock contractor selection across Generator Permit form cards by NAME.
  // This is a stopgap until the separate contractors flow is finished.
  // To revert to the original behavior, remove this block and the `disabled` logic below.
  const lockedContractorName = useMemo(() => {
    if (!formContractors || !contractors?.length) return "";

    const selectedLicenseNumbers =
      Object.values(formContractors).filter(Boolean);

    if (!selectedLicenseNumbers.length) return "";

    const selectedContractor = contractors.find((contractor) => {
      const licenseNumber =
        contractor?.licenseNumber || contractor?.license_number || "";
      return licenseNumber && selectedLicenseNumbers.includes(licenseNumber);
    });

    return normalizeContractorName(
      getContractorDisplayName(selectedContractor),
    );
  }, [
    formContractors,
    contractors,
    getContractorDisplayName,
    normalizeContractorName,
  ]);

  // Check if form should show contractor dropdown
  const shouldShowContractorDropdown = useCallback(
    (formName) => {
      // Only show for Generator Permit in St. Johns County
      if (selectedPermitType !== "generator_permit") return false;

      const countyLower = county?.toLowerCase();
      const isStJohns =
        countyLower === "st_johns" ||
        countyLower === "st johns" ||
        countyLower === "st. johns";

      if (!isStJohns) return false;

      // Only show for specific forms (not generator_worksheet)
      const formsWithContractor = ["electrical_permit", "gas_permit", "noc"];
      return formsWithContractor.includes(formName);
    },
    [selectedPermitType, county],
  );

  // Handle contractor selection for a specific form
  const handleContractorChange = useCallback(
    (formName, contractorId) => {
      setFormContractors((prev) => ({
        ...prev,
        [formName]: contractorId,
      }));
    },
    [setFormContractors],
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const privProvHandleClose = () => {
    setPrivFormAnchorEl(null);
  };

  useLayoutEffect(() => {
    if (shouldOpenStormwaterRef.current && stormwaterTriggerRef.current) {
      setAnchorEl(stormwaterTriggerRef.current);
      shouldOpenStormwaterRef.current = false;
    }
  }, [selectedForms]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (cardsAreaRef.current) {
        cardsAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [selectedPermitType, selectedCity]);

  const GROUP_MAP = new Map();
  for (const group of GROUPS) {
    for (const name of group) GROUP_MAP.set(name, group);
  }

  // Helper: Check if NOC can be deselected based on current form selection
  // Returns true if NOC is optional (can be deselected), false if mandatory
  const canDeselectNoc = useCallback(
    (currentSelectedForms) => {
      const countyLower = county?.toLowerCase();
      const isStJohns =
        countyLower === "st_johns" ||
        countyLower === "st johns" ||
        countyLower === "st. johns";

      // st_johns + generator_permit: NOC is always optional
      if (isStJohns && selectedPermitType === "generator_permit") {
        return true;
      }

      // For all other cases: NOC is optional ONLY if pp_ntbo (and its bundle) is the only form selected
      // Note: noc_affidavit counts as a real form that requires NOC, so don't exclude it
      const formsWithoutNoc = currentSelectedForms.filter(
        (form) => form !== "noc",
      );

      // NTBO bundle forms that don't require NOC
      const ntboBundle = ["pp_ntbo", "pp_ntbo_cover_sheet"];

      // Check if only NTBO bundle forms are selected (pp_ntbo alone or pp_ntbo + pp_ntbo_cover_sheet)
      const onlyNtboBundleSelected =
        formsWithoutNoc.length > 0 &&
        formsWithoutNoc.every((form) => ntboBundle.includes(form));

      if (onlyNtboBundleSelected) {
        return true;
      }

      // If no forms selected (besides noc), NOC can be deselected
      if (formsWithoutNoc.length === 0) {
        return true;
      }

      // Otherwise NOC is mandatory (including when noc_affidavit is selected)
      return false;
    },
    [county, selectedPermitType],
  );

  const handleCardClick = useCallback(
    (event, item, formName) => {
      const isStormwater = item?.name === "stormwater";
      const isPrivateProvider = item?.name === "private_provider";

      if (isStormwater && !formName && !selectedForms.includes("stormwater")) {
        stormwaterTriggerRef.current = event.currentTarget;
        shouldOpenStormwaterRef.current = true;
      }

      if (
        isPrivateProvider &&
        !formName &&
        !selectedForms.includes("private_provider")
      ) {
        setPrivFormAnchorEl(event.currentTarget);
      }

      setSelectedForms((prevSelectedForms) => {
        // Helper to auto-add NOC if needed after form change
        const maybeAddNoc = (newForms) => {
          // Don't add NOC if it's already in the forms
          if (newForms.includes("noc")) {
            return newForms;
          }

          // Check if NOC should be optional (can be deselected)
          // If it's optional, don't add it
          if (canDeselectNoc(newForms)) {
            return newForms;
          }

          // Otherwise, NOC is mandatory - add it
          return ["noc", ...newForms];
        };

        // ----- bundle behavior -----
        const bundle = GROUP_MAP.get(item?.name);
        if (bundle) {
          const anySelected = bundle.some((b) => prevSelectedForms.includes(b));
          if (anySelected) {
            // remove entire bundle
            return prevSelectedForms.filter((n) => !bundle.includes(n));
          } else {
            // add entire bundle (idempotent) - but only add forms that are actually available
            const availableBundleForms = bundle.filter((formName) => {
              // Check if the form exists in availableForms
              return availableForms.some((form) => form.name === formName);
            });
            const newForms = Array.from(
              new Set([...prevSelectedForms, ...availableBundleForms]),
            );
            return maybeAddNoc(newForms);
          }
        }

        const isAlreadySelected = prevSelectedForms.includes(item?.name);

        if (isAlreadySelected) {
          if (isStormwater) {
            setStormwaterSelection(null);
          }
          if (isPrivateProvider) {
            setPrivateProviderSignerType(null);
          }

          return prevSelectedForms.filter((name) => name !== item?.name);
        } else {
          if (isStormwater) {
            if (!formName) {
              return [...prevSelectedForms]; // wait for subtype
            } else {
              const newForms = [...prevSelectedForms, item.name]; // always "stormwater"
              return maybeAddNoc(newForms);
            }
          }

          if (isPrivateProvider && !formName) {
            return [...prevSelectedForms]; // wait for signer type
          }

          const newForms = [...prevSelectedForms, item.name];
          return maybeAddNoc(newForms);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedForms,
      setSelectedForms,
      setStormwaterSelection,
      setPrivateProviderSignerType,
      setPrivFormAnchorEl,
      availableForms,
      canDeselectNoc,
    ],
  );

  return (
    <>
      <Box>
        {(() => {
          // Precompute counts for header display
          let totalForms;

          // If we have grouped forms (Generator Permit case), count forms in all groups
          if (groupedForms) {
            totalForms = Object.values(groupedForms).reduce((total, forms) => {
              return total + (Array.isArray(forms) ? forms.length : 0);
            }, 0);
          } else {
            // Otherwise use available forms, excluding NOC when attaching new permit
            totalForms = (availableForms || []).filter(
              (item) => !(attachingNewPermit?.started && item?.name === "noc"),
            ).length;
          }

          const selectedBase = selectedForms.length;
          const shouldSubtractForInitialNoc =
            !selectedPermitType && !attachingNewPermit?.started;
          const selectedCount = Math.max(
            0,
            selectedBase - (shouldSubtractForInitialNoc ? 1 : 0),
          );
          // Expose via ref-like props for inline use below
          // eslint-disable-next-line react-hooks/exhaustive-deps
          SelectFormsStep.__counts = { selectedCount, totalForms };
          return null;
        })()}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            mt: 1,
            mb: 2,
          }}
        >
          {/* Permit Types */}
          <FormControl fullWidth variant="filled" sx={{ width: "18rem" }}>
            <InputLabel>Permit Type</InputLabel>
            <Select
              size="small"
              value={(function () {
                const opts = (availablePermitTypes || []).map((i) => i.name);
                // Only return selectedPermitType if it's in the available options and options exist
                return opts.length > 0 && opts.includes(selectedPermitType)
                  ? selectedPermitType
                  : "";
              })()}
              renderValue={(selected) => {
                const selectedOption = availablePermitTypes?.find(
                  (item) => item.name === selected,
                );
                return (
                  <Typography sx={{ fontWeight: "bold", fontSize: "1.3rem" }}>
                    {selectedOption?.label || selected}
                  </Typography>
                );
              }}
              onChange={(e) => {
                setSelectedForms([]);
                setStormwaterSelection(null);
                setSelectedPermitType(e.target.value);
                // setSelectedCity("");
                if (!attachingNewPermit?.started) {
                  const nocForm = availableForms.find(
                    (form) => form.name === "noc",
                  );
                  if (nocForm && Array.isArray(nocForm.img)) {
                    const updatedImageUrls = nocForm?.img?.map(
                      (img) => `${process.env.REACT_APP_URL}${img}`,
                    );
                    setDisplayImage(updatedImageUrls);
                  }
                } else {
                  const signed =
                    attachingNewPermit?.packageSnapshot?.documents?.noc
                      ?.signed || [];
                  if (Array.isArray(signed) && signed.length > 0) {
                    setDisplayImage(signed);
                  }
                }
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    marginTop: "1.5px",
                    maxHeight: 150, // Set the desired max height for the dropdown menu
                    width: "auto",
                    maxWidth: "100%",
                  },
                },
              }}
            >
              {availablePermitTypes?.map((item, i) => {
                const alreadyUsed =
                  attachingNewPermit?.started &&
                  Array.isArray(attachingNewPermit?.addedPermits) &&
                  attachingNewPermit.addedPermits.includes(item.name);
                return (
                  <MenuItem
                    key={i}
                    value={item.name}
                    sx={{
                      fontWeight:
                        selectedPermitType === item ? "bold" : "normal",
                      fontSize:
                        selectedPermitType === item ? "1.1rem" : "inherit",
                      opacity: alreadyUsed ? 0.4 : 1,
                    }}
                    disabled={alreadyUsed}
                  >
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Cities */}
          <FormControl
            fullWidth
            disabled={cities?.length === 0}
            variant="filled"
            sx={{ width: "18rem" }}
          >
            <InputLabel id="remove-NumCounties">
              {cities?.length === 0
                ? "No Cities Available"
                : attachingNewPermit?.started
                  ? "City (Locked to Main Permit)"
                  : "Available Cities"}
            </InputLabel>
            <Select
              size="small"
              disabled={attachingNewPermit?.started}
              value={(function () {
                const opts = Array.isArray(cities) ? cities : [];
                // For additional permits, use the main permit's city (already formatted)
                if (attachingNewPermit?.started) {
                  return selectedCity || "";
                }
                return opts.includes(selectedCity) ? selectedCity : "";
              })()}
              labelId="remove-NumCounties"
              label="removeNumCounties"
              onChange={(e) => {
                // Don't allow city change when attaching additional permit
                if (attachingNewPermit?.started) {
                  return;
                }
                setSelectedForms([]);
                setStormwaterSelection(null);
                setSelectedCity(e.target.value);
                if (!attachingNewPermit?.started) {
                  const nocForm = availableForms.find(
                    (form) => form.name === "noc",
                  );
                  if (nocForm && Array.isArray(nocForm.img)) {
                    const updatedImageUrls = nocForm?.img?.map(
                      (img) => `${process.env.REACT_APP_URL}${img}`,
                    );
                    setDisplayImage(updatedImageUrls);
                  }
                } else {
                  const signed =
                    attachingNewPermit?.packageSnapshot?.documents?.noc
                      ?.signed || [];
                  if (Array.isArray(signed) && signed.length > 0) {
                    setDisplayImage(signed);
                  }
                }
              }}
              renderValue={(selected) => (
                <Typography sx={{ fontWeight: "bold", fontSize: "1.3rem" }}>
                  {selected}
                </Typography>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    marginTop: "1.5px",
                    maxHeight: 150, // Set the desired max height for the dropdown menu
                    width: "auto",
                    maxWidth: "100%",
                  },
                },
              }}
              endAdornment={
                selectedCity &&
                !attachingNewPermit?.started && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        if (attachingNewPermit?.started) {
                          setSelectedForms([]);
                        } else {
                          // Don't auto-select NOC for Generator Permit
                          if (selectedPermitType === "generator_permit") {
                            setSelectedForms([]);
                          } else {
                            setSelectedForms(["noc"]);
                          }
                        }
                        setSelectedCity("");

                        if (!attachingNewPermit?.started) {
                          const nocForm = availableForms.find(
                            (form) => form.name === "noc",
                          );
                          if (nocForm?.img?.length) {
                            const updatedImageUrls = nocForm?.img?.map(
                              (img) => `${process.env.REACT_APP_URL}${img}`,
                            );
                            setDisplayImage(updatedImageUrls);
                          } else {
                            setDisplayImage([]);
                          }
                        }
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              IconComponent={selectedCity ? () => null : ArrowDropDownIcon}
            >
              {cities?.map(function (item, i) {
                const toTitleCase = (str) => {
                  return str.replace(/\w\S*/g, (txt) => {
                    return (
                      txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
                    );
                  });
                };
                return (
                  <MenuItem
                    key={i}
                    value={item}
                    sx={{
                      fontWeight: selectedCity === item ? "bold" : "normal",
                      fontSize: selectedCity === item ? "1.1rem" : "inherit",
                    }}
                  >
                    {toTitleCase(item)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            borderBottom: "3px solid #d3d3d3",
          }}
        />
        {countyLoading ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              mt: 10,
            }}
          >
            <CircularProgress color="warning" size="8rem" />
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                <Typography mt={1} mb={1} fontWeight="bold" align="center">
                  Selected{" "}
                  {SelectFormsStep.__counts?.selectedCount ??
                    selectedForms.length}
                  /
                  {SelectFormsStep.__counts?.totalForms ??
                    availableForms.length}
                </Typography>
                <Button
                  onClick={() => {
                    // Select All: Get all available form names
                    let allFormNames = [];

                    if (groupedForms) {
                      // If grouped, collect all forms from all groups
                      Object.values(groupedForms).forEach((forms) => {
                        forms
                          ?.filter((item) => {
                            // Hide NOC and NOC Affidavit for additional permits
                            if (
                              attachingNewPermit?.started &&
                              (item?.name === "noc" ||
                                item?.name === "noc_affidavit")
                            ) {
                              return false;
                            }
                            return true;
                          })
                          ?.forEach((item) => {
                            if (item?.name) {
                              allFormNames.push(item.name);
                            }
                          });
                      });
                    } else {
                      // If not grouped, use availableForms
                      allFormNames = (availableForms || [])
                        .filter(
                          (item) =>
                            !(
                              attachingNewPermit?.started &&
                              item?.name === "noc"
                            ),
                        )
                        .map((item) => item.name)
                        .filter(Boolean);
                    }

                    // Handle bundle behavior - if a form is in a bundle, add all bundle forms
                    const bundleForms = new Set();
                    allFormNames.forEach((formName) => {
                      const bundle = GROUP_MAP.get(formName);
                      if (bundle) {
                        // Add all forms in the bundle that are available
                        bundle.forEach((bundleFormName) => {
                          if (
                            availableForms.some(
                              (form) => form.name === bundleFormName,
                            )
                          ) {
                            bundleForms.add(bundleFormName);
                          }
                        });
                      } else {
                        bundleForms.add(formName);
                      }
                    });

                    setSelectedForms(Array.from(bundleForms));
                  }}
                  disabled={!selectedPermitType}
                  size="small"
                  variant="text"
                  sx={{ fontWeight: "bold", ml: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  onClick={() => {
                    // Unselect All: Clear all selected forms
                    // Exclude NOC if it's mandatory (not generator_permit)
                    const isNocMandatory =
                      selectedPermitType !== "generator_permit";

                    if (isNocMandatory && selectedForms.includes("noc")) {
                      // Keep NOC selected if it's mandatory
                      setSelectedForms(["noc"]);
                    } else {
                      // Clear all forms
                      setSelectedForms([]);
                    }

                    // Also clear stormwater and private provider selections
                    setStormwaterSelection(null);
                    setPrivateProviderSignerType(null);
                  }}
                  disabled={!selectedPermitType}
                  size="small"
                  variant="text"
                  sx={{ fontWeight: "bold" }}
                >
                  Unselect All
                </Button>
              </Box>
              {/* TEMPLATES */}
              <Box sx={{ display: "flex", mr: 2, alignItems: "center" }}>
                <Button
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  size="small"
                  variant="contained"
                  fontWeight="bold"
                >
                  Templates
                </Button>
              </Box>
              {/* ######### */}
            </Box>

            <Box
              ref={cardsAreaRef}
              sx={{
                height: "calc(100vh - 400px)",
                overflowY: "auto",
              }}
            >
              {/* Show grouped view if available */}
              {groupedForms ? (
                Object.entries(groupedForms)?.map(([sectionTitle, forms]) => (
                  <Box
                    key={sectionTitle}
                    sx={{
                      mb: 4,
                      borderTop:
                        sectionTitle === "Others"
                          ? "3px solid #d3d3d3"
                          : undefined,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      mb={2}
                      mt={3}
                      sx={{ px: 1 }}
                    >
                      {sectionTitle === "Others"
                        ? "Optional Forms"
                        : sectionTitle}
                    </Typography>
                    <Grid container spacing={2}>
                      {forms
                        ?.filter((item) => {
                          // Hide NOC and NOC Affidavit for additional permits
                          if (
                            attachingNewPermit?.started &&
                            (item?.name === "noc" ||
                              item?.name === "noc_affidavit")
                          )
                            return false;
                          return true;
                        })
                        ?.map((item, index) => (
                          <Grid
                            size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                            key={`${sectionTitle}-${index}`}
                          >
                            <Card
                              sx={{
                                cursor:
                                  // NOC is clickable only if it can be deselected
                                  item?.name === "noc" &&
                                  !canDeselectNoc(selectedForms)
                                    ? "normal"
                                    : "pointer",
                                border: "3px solid #1976d2",
                                p: 1,
                                minHeight: "14rem",
                                backgroundColor: selectedForms.includes(
                                  item?.name,
                                )
                                  ? "rgba(56, 142, 60, 0.3)"
                                  : "#fff",
                              }}
                              onClick={(event) => {
                                // NOC can only be clicked if it's optional
                                if (
                                  item?.name === "noc" &&
                                  !canDeselectNoc(selectedForms)
                                )
                                  return;
                                handleCardClick(event, item, null);
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="140"
                                image={`${process.env.REACT_APP_URL}${item?.cover_img}`}
                                alt={item.label}
                              />
                              <CardContent sx={{ px: 0, pt: 1, pb: 1 }}>
                                <Typography
                                  align="center"
                                  variant="body2"
                                  color="primary"
                                  fontWeight="bold"
                                >
                                  {item.name === "stormwater" &&
                                  stormwaterSelection
                                    ? item?.forms?.find(
                                        (storm) =>
                                          storm?.name === stormwaterSelection,
                                      )?.label
                                    : item.name === "private_provider" &&
                                        privateProviderSignerType
                                      ? item.label +
                                        " " +
                                        privateProviderSignerType
                                      : item.label}
                                </Typography>

                                {/* Contractor Dropdown for Generator Permit in St. Johns */}
                                {shouldShowContractorDropdown(item?.name) && (
                                  <Box
                                    sx={{ mt: 2, px: 1 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      variant="filled"
                                    >
                                      <InputLabel
                                        id={`contractor-select-${item?.name}`}
                                      >
                                        Select Contractor
                                      </InputLabel>
                                      <Select
                                        labelId={`contractor-select-${item?.name}`}
                                        id={`contractor-${item?.name}`}
                                        value={
                                          formContractors?.[item?.name] || ""
                                        }
                                        label="Select Contractor"
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleContractorChange(
                                            item?.name,
                                            e.target.value,
                                          );
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MenuItem value="">
                                          <em>None</em>
                                        </MenuItem>
                                        {getFilteredContractors(
                                          item?.name,
                                        )?.map((contractor, idx) => {
                                          const licenseNumber =
                                            contractor?.licenseNumber ||
                                            contractor?.license_number ||
                                            "";
                                          const contractorDisplayName =
                                            getContractorDisplayName(
                                              contractor,
                                            );
                                          const normalizedName =
                                            normalizeContractorName(
                                              contractorDisplayName,
                                            );

                                          // TEMPORARY: Disable contractors that do not match the locked name.
                                          // This enforces same-contractor-by-name across form cards.
                                          const isLockedOut =
                                            lockedContractorName &&
                                            normalizedName &&
                                            normalizedName !==
                                              lockedContractorName;
                                          return (
                                            <MenuItem
                                              key={licenseNumber || idx}
                                              value={licenseNumber}
                                              disabled={isLockedOut}
                                            >
                                              {contractorDisplayName}
                                              {licenseNumber
                                                ? ` (${licenseNumber})`
                                                : ""}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                ))
              ) : availableForms.length > 0 && selectedPermitType ? (
                <Grid container spacing={2} mt={2} mb={2}>
                  {availableForms
                    ?.filter((item) => {
                      if (
                        attachingNewPermit?.started &&
                        (item?.name === "noc" || item?.name === "noc_affidavit")
                      )
                        return false;
                      return true;
                    })
                    ?.map((item, index) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                        <Card
                          sx={{
                            cursor:
                              // NOC is clickable only if it can be deselected
                              item?.name === "noc" &&
                              !canDeselectNoc(selectedForms)
                                ? "normal"
                                : "pointer",
                            border: "3px solid #1976d2",
                            p: 1,
                            minHeight: "14rem",
                            backgroundColor: selectedForms.includes(item?.name)
                              ? "rgba(56, 142, 60, 0.3)"
                              : "#fff",
                          }}
                          onClick={(event) => {
                            // NOC can only be clicked if it's optional
                            if (
                              item?.name === "noc" &&
                              !canDeselectNoc(selectedForms)
                            )
                              return;
                            handleCardClick(event, item, null);
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="140"
                            image={`${process.env.REACT_APP_URL}${item?.cover_img}`}
                            alt={item.label}
                          />
                          <CardContent sx={{ px: 0, pt: 1, pb: 1 }}>
                            <Typography
                              align="center"
                              variant="body2"
                              color="primary"
                              fontWeight="bold"
                            >
                              {item.name === "stormwater" && stormwaterSelection
                                ? item?.forms?.find(
                                    (storm) =>
                                      storm?.name === stormwaterSelection,
                                  )?.label
                                : item.name === "private_provider" &&
                                    privateProviderSignerType
                                  ? item.label + " " + privateProviderSignerType
                                  : item.label}
                            </Typography>

                            {/* Contractor Dropdown for Generator Permit in St. Johns */}
                            {shouldShowContractorDropdown(item?.name) && (
                              <Box
                                sx={{ mt: 2, px: 1 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FormControl fullWidth size="small">
                                  <InputLabel
                                    id={`contractor-select-${item?.name}`}
                                  >
                                    Select Contractor
                                  </InputLabel>
                                  <Select
                                    labelId={`contractor-select-${item?.name}`}
                                    id={`contractor-${item?.name}`}
                                    value={formContractors?.[item?.name] || ""}
                                    label="Select Contractor"
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleContractorChange(
                                        item?.name,
                                        e.target.value,
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {getFilteredContractors(item?.name)?.map(
                                      (contractor, idx) => {
                                        const licenseNumber =
                                          contractor?.licenseNumber ||
                                          contractor?.license_number ||
                                          "";
                                        const contractorDisplayName =
                                          getContractorDisplayName(contractor);
                                        const normalizedName =
                                          normalizeContractorName(
                                            contractorDisplayName,
                                          );

                                        // TEMPORARY: Disable contractors that do not match the locked name.
                                        // This enforces same-contractor-by-name across form cards.
                                        const isLockedOut =
                                          lockedContractorName &&
                                          normalizedName &&
                                          normalizedName !==
                                            lockedContractorName;
                                        return (
                                          <MenuItem
                                            key={licenseNumber || idx}
                                            value={licenseNumber}
                                            disabled={isLockedOut}
                                          >
                                            {contractorDisplayName}
                                            {licenseNumber
                                              ? ` (${licenseNumber})`
                                              : ""}
                                          </MenuItem>
                                        );
                                      },
                                    )}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              ) : (
                availableForms.length === 0 &&
                selectedCity && (
                  <Typography
                    align="center"
                    my={3}
                    variant="h6"
                    sx={{ color: "red" }}
                  >
                    Currently, there are no documents available
                  </Typography>
                )
              )}

              {/* Popover for stormwater form selection */}
              <Popover
                // id={id}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    padding: 2,
                    border: "3px solid #d9e6f4",
                  }}
                >
                  <Typography
                    color="primary"
                    variant="h6"
                    align="center"
                    mb={1}
                  >
                    Select Form Type
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {stormwaterList?.map((formName) => (
                      <Button
                        key={formName}
                        variant="outlined"
                        onClick={(event) => {
                          setStormwaterSelection(stormwaterMapping[formName]);
                          handleCardClick(
                            event,
                            { name: "stormwater" },
                            formName,
                          );
                          handleClose();
                        }}
                        fullWidth
                      >
                        {formName}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Popover>

              <Popover
                open={Boolean(privFormAnchorEl)}
                anchorEl={privFormAnchorEl}
                onClose={privProvHandleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    padding: 2,
                    border: "3px solid #d9e6f4",
                  }}
                >
                  <Typography
                    color="primary"
                    variant="h6"
                    align="center"
                    mb={1}
                  >
                    Select Signer Type
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {privProviderList?.map((signerType, index) => (
                      <Button
                        variant="outlined"
                        key={index}
                        onClick={(event) => {
                          setPrivateProviderSignerType(signerType);
                          handleCardClick(
                            event,
                            {
                              name: "private_provider",
                            },
                            {
                              name: "private_provider",
                            },
                          );
                          privProvHandleClose();
                        }}
                        fullWidth
                      >
                        {signerType}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Popover>
            </Box>
          </Box>
        )}
      </Box>
      <LoadPermitTemplateModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        selectedForms={selectedForms}
        setSelectedForms={setSelectedForms}
        city={selectedCity}
        setCity={setSelectedCity}
        permitType={selectedPermitType}
        setPermitType={setSelectedPermitType}
        county={county}
      />
    </>
  );
};

export default SelectFormsStep;
